/**
 * Folder Permission Adapter
 * Wraps FolderService methods to provide unified permission interface
 */
import FolderService from '../../FolderService';

const FolderPermissionAdapter = {
  /**
   * Get all users with access to a folder
   * @param {string} folderId - Folder ID
   * @returns {Promise<Array>} Array of folder users with User data
   */
  async getUsers(folderId) {
    const folderUsers = await FolderService.getFolderUsers(folderId);
    // Transform to unified format
    return folderUsers.map((folderUser) => ({
      id: folderUser.id,
      userId: folderUser.userId,
      resourceId: folderId,
      resourceType: 'folder',
      role: folderUser.userRole,
      user: folderUser.User,
      // Keep original data for compatibility
      _original: folderUser,
    }));
  },

  /**
   * Assign role to user(s) in a folder
   * @param {string} folderId - Folder ID
   * @param {string|Array<string>} emails - Email(s) of user(s) to assign
   * @param {string} role - Role to assign
   * @param {Object} options - Additional options
   * @param {string} options.message - Optional message
   * @param {string} options.meetingId - Optional meeting ID
   * @returns {Promise<Object>} Response data
   */
  assignRole(folderId, emails, role, options = {}) {
    const emailsList = Array.isArray(emails) ? emails : [emails];
    
    return FolderService.shareFolder({
      folderId,
      emails: emailsList,
      userRole: role,
      message: options.message || '',
      meetingId: options.meetingId,
    });
  },

  /**
   * Update user's role in a folder
   * @param {string} folderId - Folder ID
   * @param {string} userId - User ID
   * @param {string} role - New role
   * @returns {Promise<Object>} Updated folder user data
   */
  updateRole(folderId, userId, role) {
    return FolderService.updateFolderUser({
      folderId,
      userId,
      userRole: role,
    });
  },

  /**
   * Remove user access from a folder
   * @param {string} folderId - Folder ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response data
   */
  removeUser(folderId, userId) {
    return FolderService.deleteFolderUser({
      folderId,
      userId,
    });
  },
};

export default FolderPermissionAdapter;

